import { useEffect, useState } from "react";
import { Download, Loader2, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/apiAdmin";
import { HUD, downloadFile, fmtMoney } from "./theme";
import { SavedCards } from "./JarvisSavedCards";
import { CallLog } from "./JarvisCallLog";

const inputCls = `h-10 rounded-xl px-3 text-sm border ${HUD.input}`;
const err = (e) => toast.error(e?.response?.data?.detail || "Failed");
const BLANK = { name: "", phone: "", email: "", company: "", address: "", type: "customer", notes: "" };

export const TypeSelect = ({ types, value, onChange, testid }) => (
  <select value={value || "customer"} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={`${inputCls} h-9 text-xs`}>
    {value && !types.some((t) => t.id === value) && <option value={value}>{value} (removed type)</option>}
    {types.map((t) => <option key={t.id} value={t.id}>{t.label}{t.discount_pct ? ` (-${t.discount_pct}%)` : ""}</option>)}
  </select>
);

export const statement = (key, name) =>
  downloadFile(`/admin/jarvis/contacts/${encodeURIComponent(key)}/statement`, {}, `statement-${(name || "contact").replace(/\W+/g, "-")}.pdf`).catch(() => toast.error("No invoices for this contact yet"));

const AddContact = ({ types, onDone }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(BLANK);
  const save = () => apiPost("/admin/jarvis/contacts", f).then(() => { toast.success("Contact saved"); setF(BLANK); setOpen(false); onDone(); }).catch(err);
  if (!open) return <button onClick={() => setOpen(true)} className={`${HUD.btn} h-10 px-4 text-sm flex items-center gap-2`} data-testid="jarvis-contact-add-open"><Plus className="w-4 h-4" /> Add contact</button>;
  return (
    <div className={`${HUD.panel} p-4 space-y-3 w-full`} data-testid="jarvis-contact-form">
      <div className="flex justify-between items-center"><p className={HUD.label}>New contact</p><button onClick={() => setOpen(false)} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button></div>
      <div className="grid sm:grid-cols-3 gap-2">
        {[["name", "Name *"], ["phone", "Phone"], ["email", "Email"], ["company", "Company (contractors / fleets)"], ["address", "Address"]].map(([k, ph]) => (
          <input key={k} placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`jarvis-contact-${k}`} className={inputCls} />
        ))}
        <TypeSelect types={types} value={f.type} onChange={(v) => setF({ ...f, type: v })} testid="jarvis-contact-type" />
      </div>
      <input placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="jarvis-contact-notes" className={`${inputCls} w-full`} />
      <button onClick={save} disabled={!f.name.trim()} className={`${HUD.btn} h-10 px-5 text-sm disabled:opacity-40`} data-testid="jarvis-contact-save">Save contact</button>
    </div>
  );
};

const Stats = ({ s }) => (
  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="jarvis-customer-stats">
    {[["Billed", s.billed, "text-white"], ["Paid", s.paid, "text-emerald-300"], ["Owed", s.owed, "text-amber-300"], ["Your costs", s.cost, "text-rose-300"], ["Profit (paid)", s.profit, "text-cyan-300"]].map(([l, v, c]) => (
      <div key={l} className="rounded-lg border border-cyan-400/10 p-2"><p className="text-[10px] text-gray-500 uppercase tracking-widest">{l}</p><p className={`text-sm font-semibold ${c}`}>{fmtMoney(v)}</p></div>
    ))}
  </div>
);

const Customer = ({ c, types, reload }) => {
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState({ name: c.name || "", phone: c.phones[0] || "", company: c.company || "", notes: c.notes || "", email: c.emails[0] || "", address: c.addresses[0] || "" });
  useEffect(() => { setEdit({ name: c.name || "", phone: c.phones[0] || "", company: c.company || "", notes: c.notes || "", email: c.emails[0] || "", address: c.addresses[0] || "" }); }, [c.name, c.phones, c.company, c.notes, c.emails, c.addresses]);
  const key = c.contact_key || c.key;
  const patch = (body, msg) => apiPatch(`/admin/jarvis/contacts/${encodeURIComponent(key)}`, { name: c.name || undefined, phone: c.phones[0] || undefined, ...body })
    .then(() => { toast.success(msg); reload(); }).catch(err);
  return (
    <div className="p-4" data-testid={`jarvis-customer-${c.key}`}>
      <div className="w-full flex flex-wrap items-center gap-3">
        <button onClick={() => setOpen(!open)} className="flex-1 min-w-[180px] text-left">
          <p className="text-white">{c.name || c.key}{c.company && <span className="text-gray-400 text-sm"> · {c.company}</span>}</p>
          <p className="text-xs text-gray-400">{[c.phones[0], c.vehicles[0], c.addresses[0]].filter(Boolean).join(" · ")}</p>
        </button>
        <TypeSelect types={types} value={c.type} onChange={(v) => patch({ type: v }, `Now a ${types.find((t) => t.id === v)?.label}`)} testid={`jarvis-customer-type-${c.key}`} />
        <div className="text-right w-36">
          <p className="text-emerald-300 text-sm font-semibold">{fmtMoney(c.total_paid)} paid</p>
          <p className="text-[11px] text-gray-500">{c.visits.length} visit(s){c.last_visit ? ` · last ${c.last_visit}` : ""}</p>
        </div>
      </div>
      {open && (
        <div className="mt-3 space-y-3 text-xs text-gray-300">
          {c.invoice_stats?.count > 0 && <Stats s={c.invoice_stats} />}
          <div className="grid sm:grid-cols-2 gap-2">
            {[["name", "Name"], ["phone", "Phone"], ["company", "Company"], ["email", "Email"], ["address", "Address"], ["notes", "Notes"]].map(([k, ph]) => (
              <input key={k} placeholder={ph} value={edit[k]} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} data-testid={`jarvis-customer-${k}-${c.key}`} className={`${inputCls} h-9 text-xs`} />
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => patch(edit, "Saved")} className={`${HUD.ghost} h-9 px-4 text-xs`} data-testid={`jarvis-customer-save-${c.key}`}>Save details</button>
            {c.saved && <button onClick={() => window.confirm(`Remove saved contact ${c.name}? (bookings & invoices stay)`) && apiDelete(`/admin/jarvis/contacts/${encodeURIComponent(key)}`).then(() => { toast.success("Removed"); reload(); }).catch(err)} className="h-9 px-4 text-xs text-gray-400 hover:text-rose-300" data-testid={`jarvis-customer-delete-${c.key}`}>Delete</button>}
            {c.invoice_stats?.count > 0 && <button onClick={() => statement(key, c.company || c.name)} className={`${HUD.ghost} h-9 px-4 text-xs flex items-center gap-1`} data-testid={`jarvis-customer-statement-${c.key}`}><Download className="w-3.5 h-3.5" /> Statement PDF</button>}
          </div>
          <SavedCards contactKey={key} />
          {c.vehicles.length > 0 && <p><span className={HUD.label}>Vehicles</span> {c.vehicles.join(", ")}</p>}
          {c.tips > 0 && <p><span className={HUD.label}>Tips</span> {fmtMoney(c.tips)}</p>}
          {c.visits.length > 0 && (
            <div className="rounded-lg border border-cyan-400/10 divide-y divide-cyan-400/5">
              {c.visits.map((v) => (
                <div key={v.booking_id} className="p-2 flex justify-between gap-2">
                  <span>{v.date} · {v.services.join(" + ") || "Service"} · {v.status}</span>
                  <span className={v.paid ? "text-emerald-300" : "text-amber-300"}>{fmtMoney(v.total)}</span>
                </div>
              ))}
            </div>
          )}
          <CallLog phones={c.phones} testid={c.key} />
        </div>
      )}
    </div>
  );
};

export const JarvisCustomers = () => {
  const [items, setItems] = useState(null);
  const [types, setTypes] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [tick, setTick] = useState(0);
  useEffect(() => { apiGet("/admin/jarvis/contact-types").then((d) => setTypes(d.items)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => apiGet("/admin/jarvis/customers", { ...(q ? { q } : {}), ...(type ? { type } : {}) }).then((d) => setItems(d.items)), 250);
    return () => clearTimeout(t);
  }, [q, type, tick]);
  const reload = () => setTick((x) => x + 1);
  return (
    <div className="space-y-4" data-testid="jarvis-customers">
      <div className="flex flex-wrap gap-3 items-start">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-3" />
          <input placeholder="Search name, company, phone, car, address…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="jarvis-customers-search" className={`w-full h-10 rounded-xl pl-9 pr-3 text-sm border ${HUD.input}`} />
        </div>
        <AddContact types={types} onDone={reload} />
      </div>
      <div className="flex flex-wrap gap-1" data-testid="jarvis-customers-type-filter">
        {[{ id: "", label: "All" }, ...types].map((t) => (
          <button key={t.id || "all"} onClick={() => setType(t.id)} data-testid={`jarvis-customers-filter-${t.id || "all"}`}
            className={`h-8 px-3 rounded-full text-xs border transition-colors ${type === t.id ? "border-cyan-400 bg-cyan-400/15 text-white" : "border-white/10 text-gray-400 hover:text-white"}`}>{t.label}</button>
        ))}
      </div>
      {!items ? <Loader2 className="w-6 h-6 animate-spin text-cyan-300" /> : (
        <div className={`${HUD.panel} divide-y divide-cyan-400/5`}>
          {items.length === 0 ? <p className="p-5 text-sm text-gray-500" data-testid="jarvis-customers-empty">No contacts here yet.</p> : items.map((c) => <Customer key={c.key} c={c} types={types} reload={reload} />)}
        </div>
      )}
    </div>
  );
};

export default JarvisCustomers;
